import React, { useContext, useEffect, useState } from 'react';
import {
  ScreenDetectionContext,
  ScreenSize,
} from '../../../services/screenDetection/screenDetection';
import useSearchBox from '../../../services/shortcuts/searchBoxes';
import totalItemCountStringDefault from '../../../utilities/totalItemCountStringDefault';
import s from './paging.module.css';

/**
 * Shown on hover. The filter box is the only place this syntax surfaces,
 * and it is not guessable.
 */
const filterSyntaxHelp = [
  `Every term has to match, in any field.`,
  `Fields: artist: album: title: genre: year: type:`,
  `        albumartist: composer: comment: publisher:`,
  `        trackno: disc: bpm: key:`,
  `"Quote a phrase" to keep its spaces, prefix - to exclude.`,
].join(`\n`);

interface Props {
  name?: string;
  display: boolean;
  displayBottom?: boolean;
  children: React.ReactNode;
  /**
   * Optional further check to conditionally display get remaining button
   */
  isComplete(): boolean;
  /**
   * Number of items after filtering
   */
  filteredItemCount: number;

  shufflePlaylist?(): void;

  displayGetRemaining: boolean;
  getRemaining?: (_ev: React.MouseEvent<HTMLButtonElement>) => void;

  additionalControls?: React.ReactNode;
  handleFilter?(val: string): void;
  /**
   * The filter box empties when this changes, as it does when the playlist
   * changes. For the owner to clear a filter it did not type - ctrl+L, when
   * the playing track is filtered out of view - since the text lives here.
   */
  filterResetKey?: number;
  clearPlaylist?(): void;

  totalItemCount(): number;
  totalItemCountString?: string | boolean;
  filteredItemCountString?: string | false;
}

export default function PlaylistControls({
  children,
  name,
  isComplete,
  display,
  displayBottom,
  filteredItemCount,
  totalItemCount,
  displayGetRemaining,
  getRemaining,
  additionalControls,
  handleFilter: handleFilterChange,
  filterResetKey,
  clearPlaylist,
  shufflePlaylist,
  totalItemCountString,
  filteredItemCountString,
}: Props) {
  const screenSize = useContext(ScreenDetectionContext);
  // ctrl+F lands here when the page has no search box of its own.
  const filterRef = useSearchBox('filter');

  const [filter, setFilter] = useState('');

  let leftControls: React.JSX.Element | undefined;
  let rightControls: React.JSX.Element | undefined;

  useEffect(
    function resetFilterOnPlaylistChange() {
      setFilter('');
    },
    [name, filterResetKey],
  );

  function updateFilter(ev: React.ChangeEvent<HTMLInputElement>) {
    ev.preventDefault();
    const val = ev.currentTarget.value;

    setFilter(val);
    handleFilterChange!(val);
  }

  // This way we don't have to check iterator undefined in isComplete
  if (display) {
    leftControls = (
      <div className={'button-group align-center ' + s.buttonGroup}>
        {/* 
          We don't allow filtering until all results have been retrieved
          Otherwise it mangles up paging.

          It is f.x. impossible to know how many pages there are of a filtered view
          when you don't have all results yet. This also makes next page / change page
          weird in filtered views with partial results..
        */}
        {isComplete() && handleFilterChange && (
          <input
            className={s.filter}
            ref={filterRef}
            type="text"
            name="filter"
            id="filter"
            onChange={updateFilter}
            value={filter}
            placeholder="Filter Playlist"
            title={filterSyntaxHelp}
          />
        )}
        {displayGetRemaining && !isComplete() && (
          <button className="button" onClick={getRemaining}>
            Get Remaining
          </button>
        )}
      </div>
    );
    rightControls = (
      <div className={s.rightControls}>
        {additionalControls}
        {shufflePlaylist && (
          <button className="button secondary" onClick={shufflePlaylist}>
            Shuffle Playlist
          </button>
        )}
        {clearPlaylist && (
          <button className="button alert" onClick={clearPlaylist}>
            Clear Playlist
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      {/* When displaying longer lists, 
              have navigation accessible at top and bottom */}
      {display && (
        <div className="grid-x">
          <div className={'cell large-4'}>{leftControls}</div>
          <div className="cell small-6 large-4">{name && <h3>{name}</h3>}</div>
          {screenSize === ScreenSize.XLarge && <div className="cell large-2" />}
          <div className={`cell small-6 large-${screenSize === ScreenSize.XLarge ? 2 : 4}`}>
            {rightControls}
          </div>
        </div>
      )}
      {display && children}
      {displayBottom && (
        <div className={s.bottomControls}>
          {totalItemCountString !== false &&
            (() => {
              // Not every source knows its total. SoundCloud's paging carries
              // no count at all, and YouTube's is wrong often enough that the
              // callers pass a getter which may return undefined - which used
              // to render literally, as "undefined tracks, 9 displayed". With
              // no total there is only one number worth showing.
              const total = totalItemCount();

              if (total === undefined || total === null || isNaN(total)) {
                return (
                  <span>
                    {`${filteredItemCount} `}
                    {typeof totalItemCountString === 'string'
                      ? totalItemCountString
                      : totalItemCountStringDefault(filteredItemCount)}
                  </span>
                );
              }

              return (
                <>
                  <span>
                    {`${total} `}
                    {totalItemCountString || totalItemCountStringDefault(total)}
                  </span>
                  {total !== filteredItemCount && (
                    <span>
                      {`, ${filteredItemCount} `}
                      {filteredItemCountString || `displayed`}
                    </span>
                  )}
                </>
              );
            })()}
        </div>
      )}
    </>
  );
}
